import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Store, Save, Pencil, Plus, MoreHorizontal,
  Globe, GlobeLock, Trash2, Package, Tag, Image, Loader2,
} from 'lucide-react';
import { useBuilderOverlay } from '@printtat/builder';
import { adminApi, resolveUrl } from '@/services/adminApi';
import { queryKeys } from '@/lib/queryKeys';
import { apiFetch, apiJson } from '@/lib/apiFetch';
import { formatPrice } from '@printtat/shared';
import type { CatalogProductDetail, ProductListItem } from '@printtat/shared';
import {
  PageHeader,
  PageContent,
  Button,
  IconButton,
  Label,
  TextField,
  LoadingState,
  AlertDialog,
  StatusBadge,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@printtat/ui';

export const AdminCatalogDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const isNew = id === 'new';
  const openBuilder = useBuilderOverlay((s) => s.openBuilder);

  const templateDesignIdFromBuilder = searchParams.get('templateDesignId');

  // ── Form state ──
  const [templateDesignId, setTemplateDesignId] = useState(templateDesignIdFromBuilder ?? '');
  const [displayName, setDisplayName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [flatPrice, setFlatPrice] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [generatingMockup, setGeneratingMockup] = useState(false);
  const [mockupCurrent, setMockupCurrent] = useState(false);
  const [mockupError, setMockupError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval>>(undefined);

  // ── Load products for new flow ──
  const [products, setProducts] = useState<ProductListItem[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);

  useEffect(() => {
    if (!isNew || templateDesignId) return;
    setProductsLoading(true);
    apiJson<ProductListItem[]>('/products')
      .then((data) => setProducts(data))
      .finally(() => setProductsLoading(false));
  }, [isNew, templateDesignId]);

  // ── Load existing catalog product ──
  const { data: product, isLoading } = useQuery<CatalogProductDetail>({
    queryKey: queryKeys.catalog.detail(id!),
    queryFn: () => adminApi.catalog.detail(id!),
    enabled: !isNew && !!id,
  });

  // ── Sync form with loaded data ──
  useEffect(() => {
    if (!product) return;
    setTemplateDesignId(product.templateDesignId);
    setDisplayName(product.displayName);
    setSlug(product.slug);
    setDescription(product.description ?? '');
    setFlatPrice(product.flatPrice != null ? String(product.flatPrice) : '');
  }, [product]);

  // ── Check if mockup is up to date ──
  useEffect(() => {
    if (!product?.surfaceExports || !product?.templateDesignId) return;
    apiJson<{ current?: boolean }>(`/mockup/designs/${product.templateDesignId}/is-current`, {
      method: 'POST',
      body: { surfaceExports: product.surfaceExports },
    })
      .then((data) => setMockupCurrent(data.current === true))
      .catch(() => setMockupCurrent(false));
  }, [product?.surfaceExports, product?.templateDesignId]);

  // Auto-generate slug (new only)
  useEffect(() => {
    if (!isNew) return;
    setSlug(
      displayName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, ''),
    );
  }, [displayName, isNew]);

  // ── Dirty tracking ──
  const changes = useMemo(() => {
    if (isNew || !product) return [];
    const c: Array<{ field: string; from: string; to: string }> = [];
    if (displayName !== product.displayName) c.push({ field: 'Display name', from: product.displayName, to: displayName });
    if (slug !== product.slug) c.push({ field: 'Slug', from: product.slug, to: slug });
    if ((description || '') !== (product.description ?? '')) c.push({ field: 'Description', from: product.description ?? '(empty)', to: description || '(empty)' });
    const origPrice = product.flatPrice != null ? String(product.flatPrice) : '';
    if (flatPrice !== origPrice) c.push({ field: 'Flat price', from: origPrice || 'Auto', to: flatPrice || 'Auto' });
    return c;
  }, [isNew, product, displayName, slug, description, flatPrice]);

  const isDirty = isNew ? (!!templateDesignId && !!displayName) : changes.length > 0;

  // ── Effective price calculation ──
  const effectivePrice = useMemo(() => {
    if (flatPrice) return Number(flatPrice);
    if (product) return product.price;
    return null;
  }, [flatPrice, product]);

  // ── Mutations ──
  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => adminApi.catalog.create(data),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.catalog.all() });
      navigate(`/catalog/${created.id}`, { replace: true });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => adminApi.catalog.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.catalog.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.catalog.detail(id!) });
    },
  });

  const publishMutation = useMutation({
    mutationFn: () => adminApi.catalog.publish(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.catalog.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.catalog.detail(id!) });
    },
  });

  const unpublishMutation = useMutation({
    mutationFn: () => adminApi.catalog.unpublish(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.catalog.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.catalog.detail(id!) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => adminApi.catalog.remove(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.catalog.all() });
      navigate('/catalog', { replace: true });
    },
  });

  const handleSave = () => {
    const data: Record<string, unknown> = {
      displayName,
      slug,
      description: description || undefined,
      flatPrice: flatPrice ? Number(flatPrice) : null,
    };
    if (isNew) {
      data.templateDesignId = templateDesignId;
      createMutation.mutate(data);
    } else {
      updateMutation.mutate(data);
    }
  };

  const handleDiscard = () => {
    if (!product) return;
    setDisplayName(product.displayName);
    setSlug(product.slug);
    setDescription(product.description ?? '');
    setFlatPrice(product.flatPrice != null ? String(product.flatPrice) : '');
  };

  // ── Mockup generation ──
  const surfaceKeyMap = useMemo<Record<string, string>>(() => ({
    front: 'tshirt_front_chest',
    back: 'tshirt_back_print',
    collar: 'tshirt_collar_label',
    leftSleeve: 'tshirt_left_sleeve',
    rightSleeve: 'tshirt_right_sleeve',
  }), []);

  const handleGenerateMockup = useCallback(async () => {
    if (!product?.surfaceExports || generatingMockup) return;
    setGeneratingMockup(true);
    setMockupError(null);

    try {
      const surfaces = Object.entries(product.surfaceExports)
        .filter(([key]) => surfaceKeyMap[key])
        .map(([key, url]) => ({
          surfaceKey: surfaceKeyMap[key],
          customDesignUrl: url,
        }));

      if (surfaces.length === 0) {
        setMockupError('No surface exports found — save the design in the builder first');
        setGeneratingMockup(false);
        return;
      }

      const { jobId } = await adminApi.renderQueue.submitRender({
        garmentId: 'classic_tshirt',
        designId: product.templateDesignId,
        productId: product.productId,
        surfaces,
      });

      // Poll for completion
      pollRef.current = setInterval(async () => {
        try {
          const res = await apiFetch(`/mockup/render/${jobId}`);
          if (!res.ok) return;
          const job = await res.json();

          if (job.status === 'completed') {
            clearInterval(pollRef.current);
            setGeneratingMockup(false);
            setMockupCurrent(true);
            // Update preview from rendered result
            const preview = job.result?.surfaces?.find(
              (s: Record<string, unknown>) => s.previewImageUrl && s.source !== 'default_cdn',
            )?.previewImageUrl;
            if (preview && id) {
              await adminApi.catalog.update(id, { previewUrl: preview });
              queryClient.invalidateQueries({ queryKey: queryKeys.catalog.detail(id) });
            }
          } else if (job.status === 'failed') {
            clearInterval(pollRef.current);
            setGeneratingMockup(false);
            setMockupError(job.error || 'Render failed');
          }
        } catch {
          // poll error — keep trying
        }
      }, 2000);
    } catch (err) {
      setGeneratingMockup(false);
      setMockupError(err instanceof Error ? err.message : 'Failed to submit render');
    }
  }, [product, generatingMockup, id, queryClient, surfaceKeyMap]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const error = createMutation.error || updateMutation.error;

  if (!isNew && isLoading) {
    return (
      <PageContent className="overflow-y-auto h-screen">
        <LoadingState variant="skeleton" columns={1} rows={6} />
      </PageContent>
    );
  }

  // ── New product: pick a base product first ──
  if (isNew && !templateDesignId) {
    return (
      <PageContent className="overflow-y-auto h-screen">
        <div className="flex items-start gap-3 mb-6">
          <IconButton size="sm" variant="outline" onClick={() => navigate('/catalog')}>
            <ArrowLeft size={16} />
          </IconButton>
          <PageHeader
            icon={Store}
            title="New Catalog Product"
            description="Pick a base product to start designing"
          />
        </div>

        <div className="max-w-2xl">
          {productsLoading ? (
            <LoadingState variant="skeleton" columns={1} rows={4} />
          ) : products.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">No products available.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {products.filter((p) => p.isActive).map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => openBuilder({
                    productId: p.id,
                    productSlug: p.slug,
                    onSave: (savedDesignId) => setTemplateDesignId(savedDesignId),
                  })}
                  className="flex items-center gap-3 p-3 border rounded-xl bg-white hover:border-gray-300 hover:shadow-sm transition-all text-left"
                >
                  {p.primaryImage ? (
                    <img
                      src={resolveUrl(p.primaryImage)}
                      alt=""
                      crossOrigin="anonymous"
                      className="w-14 h-14 rounded-lg object-cover bg-gray-100 shrink-0"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-lg bg-gray-100 shrink-0 flex items-center justify-center">
                      <Package size={20} className="text-gray-300" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                    <p className="text-xs text-gray-500">{p.category}</p>
                    <p className="text-xs font-medium text-gray-700 mt-0.5">{formatPrice(p.basePrice)}</p>
                  </div>
                  <Plus size={16} className="text-gray-400 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      </PageContent>
    );
  }

  // ── Main form (new with design or existing) ──
  return (
    <PageContent className="overflow-y-auto h-screen">
      {/* Sticky save bar */}
      {!isNew && isDirty && (
        <div className="sticky left-0 top-0 z-10 bg-white py-2 px-4 mb-4 rounded-xl border shadow-xs flex items-center gap-3">
          <span className="text-xs text-gray-500 mr-auto">
            Unsaved changes: {changes.map((c) => c.field.toLowerCase()).join(', ')}
          </span>
          <Button variant="outline" onClick={handleDiscard}>
            Discard
          </Button>
          <Button loading={isSaving} onClick={() => setShowConfirm(true)}>
            <Save size={14} className="mr-1.5" />
            Save Changes
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start gap-3">
        <IconButton size="sm" variant="outline" onClick={() => navigate('/catalog')}>
          <ArrowLeft size={16} />
        </IconButton>
        <PageHeader
          icon={Store}
          title={isNew ? 'New Catalog Product' : product?.displayName ?? ''}
          description={isNew ? 'Set up the catalog listing for your design' : `/${product?.slug} · ${product?.productName}`}
          actions={
            !isNew && product ? (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (product.isPublished) unpublishMutation.mutate();
                    else publishMutation.mutate();
                  }}
                  loading={publishMutation.isPending || unpublishMutation.isPending}
                >
                  {product.isPublished ? (
                    <><GlobeLock size={14} className="mr-1.5" />Unpublish</>
                  ) : (
                    <><Globe size={14} className="mr-1.5" />Publish</>
                  )}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <IconButton size="sm" variant="outline">
                      <MoreHorizontal size={16} />
                    </IconButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem className="text-red-600" onClick={() => setDeleteOpen(true)}>
                      <Trash2 size={14} className="mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : undefined
          }
        />
      </div>

      {!isNew && product && (
        <div className="mt-3">
          <StatusBadge
            label={product.isPublished ? 'Published' : 'Draft'}
            color={product.isPublished ? 'green' : 'gray'}
            size="sm"
          />
        </div>
      )}

      {/* Two-column layout */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column — form */}
        <div className="lg:col-span-2 space-y-5">
          {/* Design card */}
          <div className="border rounded-xl bg-white p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-900">Design</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openBuilder({ designId: templateDesignId })}
              >
                <Pencil size={12} className="mr-1.5" />
                Edit in Builder
              </Button>
            </div>
            {(product?.previewUrl || isNew) && (
              <div className="flex items-center gap-4">
                {product?.previewUrl ? (
                  <img
                    src={resolveUrl(product.previewUrl)}
                    alt={product.displayName}
                    crossOrigin="anonymous"
                    className="w-24 h-24 rounded-lg object-cover bg-gray-50 border"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-lg bg-gray-50 border flex items-center justify-center">
                    <Store size={24} className="text-gray-300" />
                  </div>
                )}
                <div className="text-sm text-gray-500">
                  <p>{product?.productName ?? 'Design saved'}</p>
                  <p className="text-xs text-gray-400 mt-0.5 font-mono truncate">{templateDesignId.slice(0, 12)}...</p>
                </div>
              </div>
            )}
          </div>

          {/* Listing details card */}
          <div className="border rounded-xl bg-white p-4 space-y-4">
            <h3 className="text-sm font-medium text-gray-900">Listing Details</h3>
            <div className="space-y-1.5">
              <Label>Display Name</Label>
              <TextField
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. Sunset Classic Tee"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Slug</Label>
              <TextField
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="e.g. sunset-classic-tee"
              />
              <p className="text-xs text-gray-400">URL: /catalog/{slug || '...'}</p>
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional product description..."
                rows={3}
                className="w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-200 resize-none"
              />
            </div>
          </div>

          {/* Pricing card */}
          <div className="border rounded-xl bg-white p-4 space-y-4">
            <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
              <Tag size={14} className="text-gray-400" />
              Pricing
            </h3>
            {!isNew && product && product.designPrice != null && (
              <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                <span className="text-xs text-gray-500">Design calculated price</span>
                <span className="text-sm font-semibold text-gray-900">
                  {formatPrice(product.designPrice)}
                </span>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Price Override (EGP)</Label>
              <TextField
                value={flatPrice}
                onChange={(e) => setFlatPrice(e.target.value)}
                placeholder="Leave empty to use design's calculated price"
                type="number"
              />
              <p className="text-xs text-gray-400">
                {flatPrice
                  ? `Override active — customers pay ${formatPrice(Number(flatPrice))} / unit`
                  : 'No override — using design price'}
              </p>
            </div>
            {effectivePrice != null && (
              <div className="flex items-center justify-between px-3 py-2.5 bg-green-50 border border-green-100 rounded-lg">
                <span className="text-xs font-medium text-green-700">Effective price</span>
                <span className="text-base font-bold text-green-800">
                  {formatPrice(effectivePrice)}
                  <span className="text-xs font-normal text-green-600 ml-1">/ unit</span>
                </span>
              </div>
            )}
          </div>

          {/* Variants card */}
          {!isNew && product && product.variants.length > 0 && (
            <div className="border rounded-xl bg-white p-4 space-y-3">
              <h3 className="text-sm font-medium text-gray-900">
                Available Variants ({product.variants.length})
              </h3>
              <div className="divide-y rounded-lg border">
                {product.variants.map((v) => (
                  <div key={v.id} className="flex items-center justify-between px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-4 h-4 rounded-full border border-gray-200"
                        style={{ backgroundColor: v.colorHex }}
                      />
                      <span className="text-sm text-gray-700">{v.size} / {v.color}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      {v.additionalPrice > 0 && <span>+{formatPrice(v.additionalPrice)}</span>}
                      <span>{v.stockCount} in stock</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Create button for new */}
          {isNew && (
            <div className="flex items-center gap-3">
              <Button onClick={handleSave} loading={isSaving} disabled={!templateDesignId || !displayName}>
                <Save size={14} className="mr-1.5" />
                Create Catalog Product
              </Button>
              <Button variant="outline" onClick={() => navigate('/catalog')}>
                Cancel
              </Button>
            </div>
          )}

          {error && (
            <p className="text-sm text-red-600">{(error as Error).message}</p>
          )}
        </div>

        {/* Right column — product preview */}
        <div className="lg:col-span-1">
          <div className="sticky top-4 space-y-3">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Product Preview</h3>
            <div className="border rounded-xl bg-white overflow-hidden shadow-sm">
              {/* Preview image */}
              <div className="aspect-square bg-gray-50 relative overflow-hidden">
                {product?.previewUrl ? (
                  <img
                    src={resolveUrl(product.previewUrl)}
                    alt={displayName || product.displayName}
                    crossOrigin="anonymous"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Store size={40} className="text-gray-200" />
                  </div>
                )}
                {!isNew && product && (
                  <div className="absolute top-2 left-2">
                    <StatusBadge
                      label={product.isPublished ? 'Published' : 'Draft'}
                      color={product.isPublished ? 'green' : 'gray'}
                      size="xs"
                    />
                  </div>
                )}
              </div>
              {/* Preview info */}
              <div className="p-3">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {displayName || 'Untitled product'}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {product?.productName ?? 'Product'}
                </p>
                <p className="text-sm font-semibold text-gray-900 mt-2">
                  {effectivePrice != null ? formatPrice(effectivePrice) : '—'}
                </p>
                {slug && (
                  <p className="text-[10px] text-gray-400 mt-1 font-mono">/catalog/{slug}</p>
                )}
              </div>
              {/* Generate mockup */}
              {!isNew && product?.surfaceExports && (
                <div className="px-3 pb-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={handleGenerateMockup}
                    disabled={generatingMockup || mockupCurrent}
                  >
                    {generatingMockup ? (
                      <><Loader2 size={12} className="mr-1.5 animate-spin" />Generating Mockup...</>
                    ) : mockupCurrent ? (
                      <><Image size={12} className="mr-1.5" />Mockup Up to Date</>
                    ) : (
                      <><Image size={12} className="mr-1.5" />Generate Mockup</>
                    )}
                  </Button>
                  {mockupError && (
                    <p className="text-xs text-red-600 mt-1.5">{mockupError}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Save confirmation dialog */}
      <AlertDialog
        open={showConfirm}
        onOpenChange={setShowConfirm}
        title="Save Changes"
        description="The following changes will be saved:"
        body={
          <div className="space-y-2">
            <p className="text-xs font-medium text-gray-900 mb-1">Catalog Product</p>
            <ul className="list-disc pl-5 space-y-0.5">
              {changes.map((c, i) => (
                <li key={i} className="text-sm text-gray-600">
                  {c.field}: {c.from} &rarr; {c.to}
                </li>
              ))}
            </ul>
          </div>
        }
        confirmText="Save"
        onConfirm={() => { setShowConfirm(false); handleSave(); }}
        loading={isSaving}
      />

      {/* Delete confirmation */}
      <AlertDialog
        open={deleteOpen}
        onOpenChange={(open) => { if (!open) setDeleteOpen(false); }}
        title="Delete catalog product"
        description="This will remove the product from the catalog. The underlying design will not be deleted."
        confirmText="Delete"
        onConfirm={() => deleteMutation.mutate()}
        variant="destructive"
      />
    </PageContent>
  );
};
